import React, { useState, useEffect, useCallback, useRef } from 'react';
import { listConversations, markOfferThreadRead, getOfferThread, postOfferReply, editOffer, resolveAssetUrl, acceptAndPay, createPaymentCheckout } from '../services/api';
import { toast } from 'sonner';
import PaymentMethodChoiceDialog from './PaymentMethodChoiceDialog';
import StripeCheckoutDialog from './StripeCheckoutDialog';
import useAutoRefresh from '../hooks/useAutoRefresh';
import { MessageSquare, Inbox, ArrowLeft, Send, Lock, Info, Pencil, AlertCircle, Tag } from 'lucide-react';
import { CUSTOMER_BOOKING_FEE } from '../lib/fees';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';

/**
 * MessagesInbox — central messaging hub for both customers and drivers.
 *
 * Layout:
 *   • Mobile (<md): single-pane. List, tap to open a conversation, "Back" to
 *     return to the list. Modeled on WhatsApp / Messenger.
 *   • Desktop (≥md): split-pane. Conversation list on the left, the selected
 *     chat panel on the right. Inspired by Airbnb's host inbox.
 *
 * Sort: server already orders unread → recency. We re-sort client-side after
 * marking-read so the row gracefully drops out of the unread cluster without
 * a network round-trip.
 *
 * Props
 *   role           "customer" | "driver" — controls counterpart label + send-side
 *   onUnreadChange (n) => void           — bubbles the unread count up so the
 *                                          nav badge stays in sync without an
 *                                          extra round-trip.
 */
export const MessagesInbox = ({ role, onUnreadChange }) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState(null); // conversation row
  const [threadOpenedAt, setThreadOpenedAt] = useState(null); // forces remount on row click

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listConversations({ archived: showArchived ? 1 : 0 });
      const rows = data.conversations || [];
      setConversations(rows);
      if (onUnreadChange) {
        // Always report active-inbox unread count, regardless of archive toggle.
        if (showArchived) {
          // Need a separate fetch for active unread.
          try {
            const { data: act } = await listConversations({ archived: 0, limit: 500 });
            onUnreadChange((act.conversations || []).filter(c => c.unread).length);
          } catch (_) { /* swallow */ }
        } else {
          onUnreadChange(rows.filter(c => c.unread).length);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [showArchived, onUnreadChange]);

  useEffect(() => { load(); }, [load]);

  // 20-second background refresh — faster than the global 60s cadence because
  // this is the chat surface. Auto-pauses while the tab is hidden.
  useAutoRefresh(load, 20_000);

  const openConversation = async (row) => {
    setSelected(row);
    setThreadOpenedAt(Date.now());
    if (row.unread) {
      try {
        await markOfferThreadRead(row.offer_id);
        // Optimistic: clear unread on this row locally so the count drops immediately
        setConversations(prev => prev.map(c => c.id === row.id ? { ...c, unread: false } : c));
        if (onUnreadChange) onUnreadChange((prev) => Math.max(0, (prev || 1) - 1));
      } catch (_) { /* server will reconcile on next poll */ }
    }
  };

  return (
    <div
      // On mobile, both dashboards render a fixed bottom-tab nav (~58-72px
      // tall incl. safe-area). We push the entire inbox card UP via
      // margin-bottom so the chat composer (which sits at the bottom of the
      // card) never sits behind the nav. Reset to zero on md+ where the nav
      // is at the top.
      className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm flex flex-col md:flex-row min-h-[60vh] md:min-h-[70vh] mb-[72px] md:mb-0"
      data-testid="messages-inbox"
    >
      {/* Left pane (list) — hidden on mobile when a conversation is open */}
      <aside
        className={`md:w-[360px] md:border-r border-gray-200 flex flex-col ${selected ? 'hidden md:flex' : 'flex'}`}
        data-testid="inbox-list-pane"
      >
        {/* Header */}
        <header className="px-4 sm:px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-[#2E7D32]" />
            <h2 className="text-lg font-extrabold text-gray-900" style={{ fontFamily: "'Inter', sans-serif" }}>
              Messages
            </h2>
          </div>
          <button
            type="button"
            onClick={() => { setShowArchived(s => !s); setSelected(null); }}
            className={`text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full transition-colors ${
              showArchived
                ? 'bg-[#2E7D32] text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid="inbox-archived-toggle"
          >
            {showArchived ? 'Showing archived' : 'Show archived'}
          </button>
        </header>

        {/* List */}
        <div className="flex-1 overflow-y-auto" data-testid="inbox-conversation-list">
          {loading ? (
            <div className="p-6 text-sm text-gray-500">Loading conversations…</div>
          ) : conversations.length === 0 ? (
            <div className="p-8 text-center" data-testid="inbox-empty-state">
              <div className="mx-auto w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mb-3">
                <Inbox className="w-7 h-7 text-[#2E7D32]" />
              </div>
              <p className="text-sm font-semibold text-gray-700">
                {showArchived ? 'No archived conversations.' : 'No conversations yet.'}
              </p>
              <p className="mt-1 text-xs text-gray-500 leading-relaxed">
                {showArchived
                  ? 'Conversations from completed or cancelled deliveries will appear here.'
                  : role === 'customer'
                    ? 'When drivers send you a question or reply, you’ll see it here.'
                    : 'When a customer replies to one of your bids, the thread will appear here.'}
              </p>
            </div>
          ) : (
            <ul role="list" className="divide-y divide-gray-100">
              {conversations.map((c) => (
                <ConversationRow
                  key={c.id}
                  row={c}
                  role={role}
                  active={selected?.id === c.id}
                  onClick={() => openConversation(c)}
                />
              ))}
            </ul>
          )}
        </div>
      </aside>

      {/* Right pane (chat) — full on mobile when a row is selected, side-by-side on md+ */}
      <section
        className={`flex-1 flex flex-col ${selected ? 'flex' : 'hidden md:flex'}`}
        data-testid="inbox-chat-pane"
      >
        {selected ? (
          <ChatPanel
            key={`${selected.id}-${threadOpenedAt}`}
            role={role}
            conversation={selected}
            onBack={() => setSelected(null)}
            onMessageSent={() => {
              // Locally update last_message preview to the just-sent text;
              // the next poll will reconcile and re-fetch fresh server state.
              load();
            }}
          />
        ) : (
          <div className="hidden md:flex flex-col items-center justify-center text-center p-10 flex-1 text-gray-500">
            <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mb-3">
              <MessageSquare className="w-8 h-8 text-[#2E7D32]" />
            </div>
            <p className="text-sm font-semibold text-gray-700">Select a conversation</p>
            <p className="text-xs mt-1 max-w-xs leading-relaxed">
              Pick a conversation from the list to view messages and reply. Unread threads bubble to the top.
            </p>
          </div>
        )}
      </section>
    </div>
  );
};

/* ---------- Conversation row (list item) ---------- */
const ConversationRow = ({ row, role, active, onClick }) => {
  const bid = bidSummary(role, row.price);
  const initials = (row.counterpart_name || '?')
    .split(' ').filter(Boolean).slice(0, 2).map(s => s[0].toUpperCase()).join('');
  const timeAgo = formatShortRelative(row.last_message_at);
  const previewPrefix = row.last_sender_role
    ? (row.last_sender_role === row.counterpart_role ? '' : 'You: ')
    : '';

  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors relative ${
          active ? 'bg-emerald-50/70' : row.unread ? 'bg-white hover:bg-gray-50' : 'bg-white hover:bg-gray-50'
        }`}
        data-testid={`inbox-row-${row.offer_id}`}
      >
        {/* Unread emerald rail */}
        {row.unread && (
          <span aria-hidden className="absolute left-0 top-0 bottom-0 w-1 bg-[#2E7D32]" />
        )}

        {/* Avatar — driver's real profile photo when available (customers
            see driver photos even pre-booking; names stay masked). Falls
            back to the initial-avatar for customers-as-counterparty. */}
        <div className={`relative flex-shrink-0 w-11 h-11 rounded-full overflow-hidden flex items-center justify-center font-bold text-sm ${
          row.counterpart_photo ? '' : (row.unread ? 'bg-[#2E7D32] text-white' : 'bg-gray-200 text-gray-700')
        }`}>
          {row.counterpart_photo ? (
            <img
              src={resolveAssetUrl(row.counterpart_photo)}
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (initials || '?')}
          {row.unread && (
            <span
              aria-hidden
              className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-red-500 ring-2 ring-white"
            />
          )}
        </div>

        {/* Body */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className={`truncate text-sm ${row.unread ? 'font-extrabold text-gray-900' : 'font-semibold text-gray-800'}`}>
              {row.counterpart_name}
            </p>
            <span className="ml-auto text-[11px] text-gray-500 flex-shrink-0">{timeAgo}</span>
          </div>
          {bid && (
            <div
              className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5"
              data-testid={`inbox-row-bid-${row.offer_id}`}
            >
              <Tag className="w-3 h-3 text-[#2E7D32]" aria-hidden />
              <span className="text-[11px] font-extrabold text-[#1B5E20] tabular-nums">
                {bid.label}: €{bid.amount.toFixed(2)}
              </span>
            </div>
          )}
          <p className={`mt-0.5 truncate text-xs ${row.unread ? 'text-gray-800 font-semibold' : 'text-gray-500'}`}>
            {previewPrefix}{row.last_message || '—'}
          </p>
          <div className="mt-1 flex items-center gap-1.5 flex-wrap">
            {row.locked && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-gray-100 border border-gray-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-gray-500"
                data-testid={`inbox-row-closed-${row.offer_id}`}
              >
                <Lock className="w-2.5 h-2.5" aria-hidden />
                Closed
              </span>
            )}
            <span className="text-[10px] uppercase tracking-wide font-bold text-gray-500">
              {row.title}
            </span>
            {row.delivery_ref && (
              <span className="text-[10px] font-mono text-gray-400">· {row.delivery_ref}</span>
            )}
          </div>
        </div>
      </button>
    </li>
  );
};

/* ---------- Chat panel (right pane / full screen on mobile) ---------- */
const ChatPanel = ({ role, conversation, onBack, onMessageSent }) => {
  const [loading, setLoading] = useState(true);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [editBidOpen, setEditBidOpen] = useState(false);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getOfferThread(conversation.offer_id);
      setThread(data);
    } catch (_) {
      setThread(null);
    } finally {
      setLoading(false);
      // Auto-scroll to bottom after render
      setTimeout(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
      }, 50);
    }
  }, [conversation.offer_id]);

  useEffect(() => { load(); }, [load]);

  const canReply = role === 'customer' ? !!thread?.can_customer_reply : !!thread?.can_driver_reply;
  // Drivers can edit their bid while it's still in the negotiation phase
  // (pre-payment). After acceptance the offer is locked. We rely on the
  // backend's post_payment flag — same source of truth as the chat lock.
  const canEditBid = role === 'driver' && thread && !thread.post_payment && !thread.locked;

  // Pre-payment per-side counter (6 max). We compute remaining locally so we
  // can render the live "X / 6 messages remaining" badge + 1-left warning
  // without an extra round-trip.
  const limit = thread?.message_limit_per_side ?? 6;
  const myCount = thread
    ? (role === 'customer' ? thread.customer_message_count : thread.driver_message_count)
    : 0;
  const remaining = Math.max(0, limit - myCount);
  const postPayment = !!thread?.post_payment;
  const showCounter = !postPayment && thread != null && !thread.locked;
  const chatBid = bidSummary(role, thread?.price ?? conversation.price, postPayment);

  // Customer: accept this bid and pay the booking fee without leaving the chat.
  const [choiceOpen, setChoiceOpen] = useState(false);
  const [paying, setPaying] = useState(false);
  const [checkoutSecret, setCheckoutSecret] = useState(null);
  const canAccept = role === 'customer' && !!thread?.can_accept && !!thread?.accept_offer;

  const handleAcceptAndPay = async () => {
    const offer = thread?.accept_offer;
    if (!offer || paying) return;
    setPaying(true);
    try {
      await acceptAndPay(offer.delivery_id, offer.id, 'direct');
      const { data } = await createPaymentCheckout(offer.delivery_id, window.location.origin);
      setChoiceOpen(false);
      if (data.client_secret) setCheckoutSecret(data.client_secret);
      else window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start payment. Please try again.');
    } finally {
      setPaying(false);
    }
  };

  const closeCheckout = async () => {
    setCheckoutSecret(null);
    await load();
    if (onMessageSent) onMessageSent();
  };


  const handleSend = async (e) => {
    e?.preventDefault?.();
    if (!text.trim() || sending) return;
    setSending(true);
    setError('');
    try {
      await postOfferReply(conversation.offer_id, text.trim());
      setText('');
      await load();
      if (onMessageSent) onMessageSent();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to send. Try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-[60vh] md:min-h-[70vh]" data-testid="inbox-chat">
      {/* Chat header */}
      <header className="px-3 sm:px-4 py-3 border-b border-gray-100 flex items-center gap-3 bg-gray-50/60">
        <button
          type="button"
          onClick={onBack}
          className="md:hidden p-1.5 rounded-full hover:bg-gray-200/70"
          data-testid="inbox-chat-back"
          aria-label="Back to inbox"
        >
          <ArrowLeft className="w-5 h-5 text-gray-700" />
        </button>
        {/* Header avatar — driver's photo (visible even pre-booking for
            customers) or initial-fallback for drivers seeing customer. */}
        <div className="relative w-10 h-10 rounded-full overflow-hidden bg-[#2E7D32] text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
          {conversation.counterpart_photo ? (
            <img
              src={resolveAssetUrl(conversation.counterpart_photo)}
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            (conversation.counterpart_name || '?').split(' ').filter(Boolean).slice(0, 2).map(s => s[0].toUpperCase()).join('') || '?'
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-gray-900 text-sm truncate">{conversation.counterpart_name}</p>
          <p className="text-[11px] text-gray-500 truncate">
            {conversation.title} {conversation.delivery_ref && (<span className="font-mono">· {conversation.delivery_ref}</span>)}
          </p>
        </div>
        {/* Driver: edit bid right from the chat. Customer-side counter sits here too. */}
        <div className="flex items-center gap-2">
          {showCounter && (
            <span
              className={`hidden sm:inline-flex items-center px-2 py-1 rounded-full text-[11px] font-bold ${
                remaining === 0 ? 'bg-gray-100 text-gray-500'
                : remaining === 1 ? 'bg-amber-100 text-amber-800'
                : 'bg-emerald-100 text-emerald-700'
              }`}
              data-testid="inbox-chat-counter"
            >
              {remaining} / {limit} left
            </span>
          )}
          {canEditBid && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setEditBidOpen(true)}
              className="border-[#2E7D32]/30 text-[#1B5E20] hover:bg-emerald-50 hover:border-[#2E7D32]/50 h-8 px-2.5 text-xs font-semibold rounded-full"
              data-testid="inbox-chat-edit-bid"
            >
              <Pencil className="w-3.5 h-3.5 mr-1" />
              Edit bid
            </Button>
          )}
        </div>
      </header>

      {/* Bid amount banner — always visible at the top of the thread so there's
       * no confusion about which price this conversation is about. */}
      {chatBid && (
        <div
          className="px-3 sm:px-4 py-2.5 border-b border-emerald-100 bg-emerald-50/70 flex items-center justify-between gap-3"
          data-testid="inbox-chat-bid-banner"
        >
          <div className="flex items-center gap-2 min-w-0">
            <span className="w-7 h-7 rounded-full bg-[#2E7D32]/10 flex items-center justify-center flex-shrink-0">
              <Tag className="w-3.5 h-3.5 text-[#2E7D32]" aria-hidden />
            </span>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wide font-bold text-emerald-800/80">
                {chatBid.label}
              </p>
              {chatBid.note && (
                <p className="text-[11px] text-emerald-900/70 truncate">{chatBid.note}</p>
              )}
            </div>
          </div>
          <span
            className="text-base sm:text-lg font-extrabold text-[#1B5E20] tabular-nums flex-shrink-0"
            data-testid="inbox-chat-bid-amount"
          >
            €{chatBid.amount.toFixed(2)}
          </span>
          {canAccept && (
            <Button
              type="button"
              size="sm"
              onClick={() => setChoiceOpen(true)}
              className="bg-[#2E7D32] text-white hover:bg-[#1B5E20] h-8 px-3 text-xs font-bold rounded-full flex-shrink-0"
              data-testid="inbox-chat-accept-pay"
            >
              Accept &amp; pay
            </Button>
          )}
        </div>
      )}

      {/* Inline guidance strip — explains the rules of the road. Hidden after
       * payment when the chat unlocks fully. */}
      {!postPayment && !thread?.locked && (
        <div className="px-3 sm:px-4 py-2 border-b border-amber-100 bg-amber-50/60 flex items-start gap-2" data-testid="inbox-chat-guidance">
          <Info className="w-3.5 h-3.5 text-amber-700 mt-0.5 flex-shrink-0" />
          <p className="text-[11px] text-amber-900 leading-snug">
            You can negotiate price freely in chat. {role === 'driver' && 'If a new price is agreed, tap "Edit bid" to update it. '}
            Contact details, payment apps and links to other chat apps can&apos;t be shared until a booking is confirmed.
            {showCounter && ` You have ${remaining} of ${limit} messages remaining.`}
          </p>
        </div>
      )}

      {/* Messages list */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 sm:px-4 py-4 space-y-3 bg-gray-50/40" data-testid="inbox-chat-messages">
        {loading ? (
          <p className="text-sm text-gray-500 text-center">Loading messages…</p>
        ) : !thread ? (
          <p className="text-sm text-red-500 text-center">Could not load conversation.</p>
        ) : (
          <>
            {/* Initial bid message (the driver's first quote message) */}
            {thread.bid_message && (
              <Bubble
                side={role === 'driver' ? 'right' : 'left'}
                name={thread.driver_name}
                text={thread.bid_message}
                createdAt={thread.bid_created_at}
                badge="Initial bid"
                photoUrl={role === 'customer' ? thread.driver_profile_image : null}
              />
            )}
            {(thread.messages || []).map((m, idx) => {
              // System messages — centred neutral pill (bid updated, etc.)
              if (m.sender_role === 'system') {
                return <SystemPill key={idx} text={m.text} createdAt={m.created_at} />;
              }
              return (
                <Bubble
                  key={idx}
                  side={m.sender_role === role ? 'right' : 'left'}
                  name={m.sender_name}
                  text={m.text}
                  createdAt={m.created_at}
                  photoUrl={role === 'customer' && m.sender_role === 'driver' ? (m.sender_profile_image || thread.driver_profile_image) : null}
                />
              );
            })}
            {!thread.bid_message && (thread.messages || []).length === 0 && (
              <p className="text-sm text-gray-500 text-center">No messages yet.</p>
            )}
          </>
        )}
      </div>

      {/* 1-message-remaining nudge (between thread + composer) */}
      {showCounter && remaining === 1 && canReply && (
        <div className="px-3 sm:px-4 py-2 bg-amber-50 border-t border-amber-100 flex items-center gap-2" data-testid="inbox-chat-one-left-warning">
          <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <p className="text-xs text-amber-900 leading-snug">
            <span className="font-semibold">You have 1 message remaining.</span>{' '}
            {role === 'driver'
              ? 'Make it count — after this the chat locks until the customer chooses your bid.'
              : 'After this the chat locks until you choose this driver and book.'}
          </p>
        </div>
      )}

      {/* Composer */}
      <form
        onSubmit={handleSend}
        className="px-3 sm:px-4 py-3 border-t border-gray-100 bg-white flex items-end gap-2"
        data-testid="inbox-chat-composer"
      >
        {canReply ? (
          <>
            <textarea
              rows={1}
              placeholder={role === 'customer' ? 'Reply to driver…' : 'Reply to customer…'}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              className="flex-1 resize-none rounded-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E7D32]/30 focus:border-[#2E7D32]"
              data-testid="inbox-chat-input"
            />
            {/* Mobile-visible counter (desktop already in header) */}
            {showCounter && (
              <span
                className={`sm:hidden inline-flex items-center px-2 py-1 rounded-full text-[10px] font-bold ${
                  remaining === 1 ? 'bg-amber-100 text-amber-800'
                  : 'bg-emerald-100 text-emerald-700'
                }`}
                data-testid="inbox-chat-counter-mobile"
              >
                {remaining}/{limit}
              </span>
            )}
            <Button
              type="submit"
              disabled={!text.trim() || sending}
              className="bg-[#2E7D32] text-white hover:bg-[#1B5E20] h-11 w-11 p-0 rounded-full flex items-center justify-center"
              data-testid="inbox-chat-send"
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </Button>
          </>
        ) : thread?.locked ? (
          <div className="flex-1 flex items-start gap-2 text-xs text-gray-700 px-3 py-2.5 bg-gray-50 rounded-lg" data-testid="inbox-chat-closed">
            <Lock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gray-500" />
            <div>
              <p className="font-semibold text-gray-800">Messaging closed</p>
              <p className="mt-0.5 text-gray-600">{thread.locked_reason}</p>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-start gap-2 text-xs text-gray-700 px-3 py-2.5 bg-gray-50 rounded-lg" data-testid="inbox-chat-locked">
            <Lock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gray-500" />
            <div>
              <p className="font-semibold text-gray-800">
                You have used all {limit} messages.
              </p>
              <p className="mt-0.5 text-gray-600">
                {role === 'customer'
                  ? 'Proceed with the booking to continue chatting with this driver.'
                  : 'Wait for the customer to choose your bid to continue chatting.'}
              </p>
            </div>
          </div>
        )}
      </form>
      {error && (
        <p className="px-4 pb-2 text-xs text-red-600" data-testid="inbox-chat-error">{error}</p>
      )}

      {/* Customer: accept + pay this bid in-chat */}
      {canAccept && (
        <PaymentMethodChoiceDialog
          open={choiceOpen}
          offer={thread.accept_offer}
          onCancel={() => setChoiceOpen(false)}
          onChoose={handleAcceptAndPay}
          processing={paying}
        />
      )}
      <StripeCheckoutDialog
        open={!!checkoutSecret}
        clientSecret={checkoutSecret}
        onClose={closeCheckout}
      />

      {/* Edit bid dialog (driver-only) */}
      {canEditBid && (
        <EditBidDialog
          open={editBidOpen}
          onOpenChange={setEditBidOpen}
          currentPrice={thread?.price ?? conversation.price}
          offerId={conversation.offer_id}
          onSaved={async () => {
            setEditBidOpen(false);
            await load();
            if (onMessageSent) onMessageSent();
          }}
        />
      )}
    </div>
  );
};

const SystemPill = ({ text, createdAt }) => (
  <div className="flex justify-center my-1.5" data-testid="inbox-chat-system">
    <div className="max-w-[85%] inline-flex flex-col items-center gap-0.5">
      <div className="px-3 py-1.5 rounded-full bg-amber-100/80 border border-amber-200 text-amber-900 text-xs font-semibold text-center leading-snug">
        <span className="mr-1" aria-hidden>💬</span>
        {text}
      </div>
      <span className="text-[10px] text-gray-400">{formatShortRelative(createdAt)}</span>
    </div>
  </div>
);

const EditBidDialog = ({ open, onOpenChange, currentPrice, offerId, onSaved }) => {
  const [price, setPrice] = useState(currentPrice != null ? String(currentPrice) : '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open) {
      setPrice(currentPrice != null ? String(currentPrice) : '');
      setErr('');
    }
  }, [open, currentPrice]);

  const handleSave = async (e) => {
    e?.preventDefault?.();
    setErr('');
    const p = parseFloat(price);
    if (!Number.isFinite(p) || p < 1) {
      setErr('Enter a valid price (€1 or more).');
      return;
    }
    if (Math.abs(p - Number(currentPrice || 0)) < 0.005) {
      setErr('New price must be different from the current bid.');
      return;
    }
    setSaving(true);
    try {
      await editOffer(offerId, { price: p });
      if (onSaved) await onSaved();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Could not update bid. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="edit-bid-dialog">
        <DialogHeader>
          <DialogTitle>Update your bid</DialogTitle>
          <DialogDescription>
            Set a new price for this delivery. The customer will be notified
            and a system message will appear in the chat showing the change.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSave} className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Current bid</label>
            <p className="text-sm font-bold text-gray-900">€{Number(currentPrice || 0).toFixed(2)}</p>
          </div>
          <div>
            <label htmlFor="new-bid-price" className="block text-xs font-semibold text-gray-700 mb-1">New bid (€)</label>
            <Input
              id="new-bid-price"
              type="number"
              step="0.01"
              min="1"
              autoFocus
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              data-testid="edit-bid-input"
            />
          </div>
          {err && <p className="text-xs text-red-600" data-testid="edit-bid-error">{err}</p>}
          <div className="flex gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1" data-testid="edit-bid-cancel">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="flex-1 bg-[#2E7D32] hover:bg-[#1B5E20]" data-testid="edit-bid-save">
              {saving ? 'Saving…' : 'Update bid'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Bubble = ({ side, name, text, createdAt, badge, photoUrl }) => {
  const right = side === 'right';
  return (
    <div className={`flex ${right ? 'justify-end' : 'justify-start'} gap-2 items-end`}>
      {/* Left-side photo avatar (driver photos shown to customer even
          pre-booking; falls back to nothing when no photo). */}
      {!right && photoUrl && (
        <div className="w-7 h-7 rounded-full overflow-hidden bg-gray-200 flex-shrink-0 self-end mb-4">
          <img
            src={resolveAssetUrl(photoUrl)}
            alt=""
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      )}
      <div className={`max-w-[80%] sm:max-w-[70%] ${right ? 'items-end' : 'items-start'} flex flex-col`}>
        {!right && name && (
          <span className="text-[11px] font-semibold text-gray-500 mb-0.5 px-1">{name}</span>
        )}
        <div
          className={`px-3.5 py-2 rounded-2xl text-sm leading-snug whitespace-pre-wrap break-words shadow-sm ${
            right
              ? 'bg-[#2E7D32] text-white rounded-br-md'
              : 'bg-white text-gray-900 border border-gray-200 rounded-bl-md'
          }`}
        >
          {badge && (
            <span className={`inline-block mb-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
              right ? 'bg-white/20 text-white' : 'bg-emerald-100 text-emerald-700'
            }`}>{badge}</span>
          )}
          <div>{text}</div>
        </div>
        <span className={`text-[10px] mt-0.5 px-1 ${right ? 'text-right' : 'text-left'} text-gray-400`}>
          {formatShortRelative(createdAt)}
        </span>
      </div>
    </div>
  );
};

/* ---------- Helpers ---------- */

/**
 * Bid amount shown on a conversation. Customers always see the total delivery
 * price (driver's bid + €10 booking fee) so it matches the bid cards and the
 * booking screen. Drivers see their own bid exactly as they entered it.
 */
function bidSummary(role, price, booked = false) {
  const bid = Number(price);
  if (!Number.isFinite(bid) || bid <= 0) return null;
  if (role === 'customer') {
    return {
      amount: +(bid + CUSTOMER_BOOKING_FEE).toFixed(2),
      label: booked ? 'Agreed price' : 'Their bid',
      note: `€${bid.toFixed(2)} bid + €${CUSTOMER_BOOKING_FEE} booking fee`,
    };
  }
  return {
    amount: +bid.toFixed(2),
    label: booked ? 'Agreed bid' : 'Your bid',
    note: null,
  };
}

function formatShortRelative(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString('en-IE', { day: 'numeric', month: 'short' });
}

export default MessagesInbox;
